"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { useCustomerAuth } from "@/context/auth-context";
import { AnnouncementBar } from "@/components/announcement-bar";
import { Navbar } from "@/components/navbar";
import { Footer } from "@/components/footer";
import { WhatsAppButton } from "@/components/whatsapp-button";
import { MobileTabBar } from "@/components/mobile-tab-bar";

const ease = [0.16, 1, 0.3, 1] as const;

export function ForgotPasswordPage() {
  const { forgotPassword } = useCustomerAuth();
  const t = useTranslations("account");
  const tCommon = useTranslations("common");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || submitting) return;
    setSubmitting(true);
    // Always show success — backend never reveals whether the email exists,
    // and we mirror that here so the UX is consistent.
    await forgotPassword(email);
    setSubmitting(false);
    setSubmitted(true);
  }

  return (
    <>
      <AnnouncementBar />
      <Navbar />
      <main className="flex-1">
        <div className="content-wrapper py-12 lg:py-20">
          <nav className="flex items-center gap-1.5 text-xs text-text-tertiary mb-8">
            <Link href="/" className="hover:text-foreground transition-colors">{tCommon("home")}</Link>
            <span>/</span>
            <Link href="/account/login" className="hover:text-foreground transition-colors">{t("loginBreadcrumb")}</Link>
            <span>/</span>
            <span className="text-text-secondary">{t("forgotPasswordTitle")}</span>
          </nav>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease }}
            className="max-w-md mx-auto"
          >
            <div className="text-center mb-10">
              <p className="label-sm mb-3">{t("label")}</p>
              <h1 className="heading-md">{t("forgotPasswordTitle")}</h1>
            </div>

            {submitted ? (
              <div className="space-y-6 text-center">
                <p className="text-sm text-text-secondary">
                  {t("forgotPasswordSubmitted")}
                </p>
                <Link
                  href="/account/login"
                  className="inline-block text-foreground font-medium hover:underline"
                >
                  {t("backToLogin")}
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <p className="text-sm text-text-secondary">
                  {t("forgotPasswordIntro")}
                </p>

                <div>
                  <label htmlFor="email" className="block text-xs font-medium text-text-secondary mb-1.5">
                    {t("emailField")} <span className="text-negative">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder={t("exampleEmailNl")}
                    autoComplete="email"
                    className="w-full h-11 px-3 bg-background border border-border text-sm text-foreground placeholder:text-text-tertiary outline-none focus:border-foreground transition-colors rounded-xl"
                    style={{ transitionDuration: "var(--duration-fast)" }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={!email.trim() || submitting}
                  className="w-full h-12 bg-foreground text-background label-sm hover:bg-foreground/85 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 rounded-lg"
                  style={{ transitionDuration: "var(--duration-fast)" }}
                >
                  {submitting ? t("pleaseWait") : t("sendResetLink")}
                </button>

                <p className="text-sm text-text-tertiary text-center">
                  <Link href="/account/login" className="hover:text-foreground transition-colors">
                    {t("backToLogin")}
                  </Link>
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </main>
      <Footer />
      <WhatsAppButton />
      <MobileTabBar />
    </>
  );
}
